import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';

/**
 * Rocco filter configs for the professional profile list tabs. They mirror
 * what the legacy Aria panel showed on each tab (old-tab parity), which is
 * why they differ from the directory browse configs:
 *
 * - our-products keeps Range, Pricing, Specify (downloads) and Designer,
 *   which browse hides, and drops Suppliers + location (the tab is already
 *   scoped to one professional).
 * - case-studies keeps the Project owner filter (manufacturers only — the
 *   state is only built when the professional shows it) and drops the
 *   professional-scoping sections covered by IN_PAGE_SEARCH_IGNORE_FILTER_KEYS.
 * - partners drops the Featured (hasAwards) checkbox the old panel hid via
 *   hideInFilters.
 * - journal has no facet sections: its sidebar is the article category list
 *   alone, which the sidebar draws from the category state, not the config.
 *
 * Awards sections render through the awards tree, gated by the
 * `award_badges` feature; the section disappears when the state is absent.
 */

export const PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Product',
    barFilters: [],
    modalFilters: [
        {
            title: 'Staff admin filters',
            collapsable: true,
            collapsedByDefault: true,
            adminOnly: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'invalidLink',
                    label: 'Invalid Link',
                },
                {
                    type: 'default',
                    filterKey: 'disabled',
                    label: 'Disabled',
                },
                {
                    type: 'default',
                    filterKey: 'profileOnly',
                    label: 'Profile Only',
                },
            ],
        },
        {
            title: 'Range',
            collapsable: true,
            collapsedByDefault: false,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggLibraryRanges',
                    filterKey: 'libraryRanges',
                    searchInputPlaceholder: 'Search by Range',
                    allowMultipleValues: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Colour',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'color',
                    filterKey: 'colors',
                },
            ],
        },
        {
            title: 'Material',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggProductMaterials',
                    filterKey: 'materials',
                    searchInputPlaceholder: 'Search by Name',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Size',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'simpleRange',
                    filterKey: 'lengthRange',
                    label: 'Length',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'widthRange',
                    label: 'Width',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'heightRange',
                    label: 'Height',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'diameterRange',
                    label: 'Diameter',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'weightRange',
                    label: 'Weight',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'kg',
                },
            ],
        },
        {
            title: 'Brand',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggBrands',
                    filterKey: 'brands',
                    searchInputPlaceholder: 'Search by Brand',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Designer',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggDesigners',
                    filterKey: 'designers',
                    searchInputPlaceholder: 'Search by Name',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                    columns: 1,
                },
            ],
        },
        {
            title: 'Awards',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'awards',
                },
            ],
        },
        {
            title: 'Pricing',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'isPurchasable',
                    label: 'Buy online',
                },
                {
                    type: 'default',
                    filterKey: 'isOnSale',
                    label: 'On sale (discount)',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'priceRange',
                    label: 'Price range',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: '$',
                },
            ],
        },
        {
            title: 'Specify',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'downloads',
                },
            ],
        },
        {
            title: 'Availability',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'isInStock',
                    label: 'In stock',
                },
                {
                    type: 'default',
                    filterKey: 'isDeliveryAvailable',
                    label: 'Delivery available',
                },
                {
                    type: 'default',
                    filterKey: 'isSampleAvailable',
                    label: 'Samples available',
                },
            ],
        },
    ],
};

export const PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Project',
    barFilters: [],
    modalFilters: [
        {
            title: 'Staff admin filters',
            collapsable: true,
            collapsedByDefault: true,
            adminOnly: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'disabled',
                    label: 'Disabled',
                },
                {
                    type: 'default',
                    filterKey: 'profileOnly',
                    label: 'Profile Only',
                },
            ],
        },
        {
            title: 'Project owner',
            collapsable: true,
            collapsedByDefault: false,
            filters: [
                {
                    type: 'default',
                    filterKey: 'viewProjectsBy',
                },
            ],
        },
        {
            title: 'Interactive',
            collapsable: true,
            collapsedByDefault: false,
            filters: [
                {
                    type: 'default',
                    filterKey: 'hasProducts',
                    label: 'Projects with products',
                },
                {
                    type: 'default',
                    filterKey: 'hasVideos',
                    label: 'Projects with videos',
                },
            ],
        },
        {
            title: 'Awards',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'awards',
                },
            ],
        },
    ],
};

export const PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Professional',
    barFilters: [],
    modalFilters: [
        {
            title: 'Staff admin filters',
            collapsable: true,
            collapsedByDefault: true,
            adminOnly: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'invalidLink',
                    label: 'Invalid Link',
                },
                {
                    type: 'default',
                    filterKey: 'disabled',
                    label: 'Disabled',
                },
            ],
        },
        {
            title: 'Location',
            collapsable: true,
            collapsedByDefault: false,
            filters: [
                {
                    type: 'default',
                    filterKey: 'searchLocation',
                },
            ],
        },
        {
            title: 'Business Focus',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'businessFocuses',
                },
            ],
        },
        {
            title: 'Associations',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'multiple',
                    filterKey: 'associations',
                    columns: 1,
                },
            ],
        },
        {
            title: 'Projects listed on ArchiPro',
            collapsable: true,
            collapsedByDefault: true,
            filters: [
                {
                    type: 'default',
                    filterKey: 'projectCount',
                },
            ],
        },
    ],
};

export const PROFESSIONAL_TAB_ARTICLE_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Article',
    barFilters: [],
    modalFilters: [],
};

/** The per-search-index tab configs, keyed the same way as the loaders. */
export const PROFESSIONAL_TAB_FILTERS_CONFIGS = {
    product: PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG,
    project: PROFESSIONAL_TAB_PROJECT_FILTERS_CONFIG,
    professional: PROFESSIONAL_TAB_PROFESSIONAL_FILTERS_CONFIG,
    blogpost: PROFESSIONAL_TAB_ARTICLE_FILTERS_CONFIG,
} as const;
